import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Card, Group, Skeleton, Stack, Switch, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import { IconAlertTriangle } from '@tabler/icons-react';
import DocumentGroupCard from '@/components/app/company/DocumentGroupCard';
import ExpiryDialog from '@/components/app/company/ExpiryDialog';
import { companyService } from '@/api/services/CompanyService';
import { getApiMessage, getStatus, isNetworkError, isServiceUnavailable } from '@/api/errors';
import { ALL_DOCUMENTS_QUERY_KEY, useAllDocuments } from '@/hooks/useAllDocuments';
import { useJustSignedDocTypes } from '@/hooks/useJustSignedDocTypes';
import { PROFILE_QUERY_KEY } from '@/hooks/useProfile';
import { useIsManager } from '@/hooks/useRoles';
import { REQUIRED_DATA_QUERY_KEY } from '@/hooks/useRequiredData';
import { ROUTES } from '@/routes/paths';
import type { DocFile, DocType } from '@/types/company';

// G3 — Аккредитация: флаг сельхозпроизводителя + группы документов (через DocumentGroupCard).
export default function AccreditationTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Признак сельхозпроизводителя — орг-данные: менять может только владелец-менеджер (бэкенд отдаёт 403
  // остальным). Загрузка документов аттестации не ограничена — это совместная задача организации.
  const canEdit = useIsManager();
  const { data, isLoading, isError, error } = useAllDocuments();
  const justSigned = useJustSignedDocTypes(data?.item);
  const [expiry, setExpiry] = useState<{ docId: number; initial: DocFile } | null>(null);
  const [progress, setProgress] = useState<{ docType: DocType; percent: number } | null>(null);

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ALL_DOCUMENTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: REQUIRED_DATA_QUERY_KEY });
    // Загрузка/удаление документа или смена флага производителя могут завершить аттестацию
    // (FULL_VERIFIED) → меняется profile.isVerified, от которого на «Главной» зависит переключение
    // онбординг→плитки и карточка «Добавьте сотрудников». Без этого они обновятся лишь после refresh.
    queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
  };
  const onError = (error: unknown): void => {
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(error)
        ? t('auth.errors.unavailable')
        : isNetworkError(error)
          ? t('auth.errors.generic')
          : (getApiMessage(error) ?? t('auth.errors.generic')),
    });
  };

  const producer = useMutation({
    mutationFn: (value: boolean) => companyService.setIsProducer(value),
    onSuccess: invalidate,
    onError,
  });
  const upload = useMutation({
    mutationFn: (payload: { docType: DocType; files: File[] }) =>
      companyService.uploadDocuments(payload.docType, payload.files, (percent) =>
        setProgress({ docType: payload.docType, percent }),
      ),
    onMutate: (payload) => setProgress({ docType: payload.docType, percent: 0 }),
    onSuccess: () => {
      invalidate();
      notifications.show({ color: 'lime', message: t('app.company.accreditation.uploaded') });
    },
    onError,
    onSettled: () => setProgress(null),
  });
  const remove = useMutation({
    mutationFn: (docId: number) => companyService.deleteDocument(docId),
    onSuccess: invalidate,
    onError,
  });
  const expiryMutation = useMutation({
    mutationFn: (payload: { docId: number; isPerpetual: boolean; date?: string }) =>
      companyService.setExpiredDate(payload.docId, payload.isPerpetual, payload.date),
    onSuccess: () => {
      invalidate();
      setExpiry(null);
    },
    onError,
  });

  async function download(docType: DocType, docId: number): Promise<void> {
    try {
      await companyService.downloadDocument(docType, docId);
    } catch (error) {
      onError(error);
    }
  }

  function confirmDelete(docId: number): void {
    modals.openConfirmModal({
      title: t('app.company.accreditation.deleteTitle'),
      children: <Text size="sm">{t('app.company.accreditation.deleteConfirm')}</Text>,
      labels: { confirm: t('app.company.banking.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(docId),
    });
  }

  if (isError) {
    // 424 (Failed Dependency) — не выполнено предусловие аккредитации (например, не выбрана система
    // налогообложения). Показываем реальную причину от сервера и ведём к её устранению, а не пугаем
    // генерик-ошибкой. Сеть/недоступность сервиса — по-прежнему «попробуйте позже».
    if (getStatus(error) === 424) {
      return (
        <Card withBorder padding="lg">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <IconAlertTriangle
              size={20}
              color="var(--mantine-color-yellow-7)"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Stack gap={6}>
              <Text>{getApiMessage(error) ?? t('app.company.accreditation.preconditionFallback')}</Text>
              <Anchor component={Link} to={`${ROUTES.APP_COMPANY}#tax`} size="sm" fw={500} c="brand.7">
                {t('app.company.accreditation.preconditionCta')}
              </Anchor>
            </Stack>
          </Group>
        </Card>
      );
    }
    return (
      <Card withBorder padding="lg">
        <Text c="red">
          {isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}
        </Text>
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <Stack gap="sm">
        <Skeleton h={48} />
        <Skeleton h={160} />
        <Skeleton h={160} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder padding="md">
        <Switch
          label={t('app.company.accreditation.producer')}
          checked={data.agriculturalProducer}
          disabled={producer.isPending || !canEdit}
          onChange={(event) => producer.mutate(event.currentTarget.checked)}
        />
      </Card>

      {data.item.map((group) => (
        <DocumentGroupCard
          key={group.docType}
          group={group}
          uploadPending={upload.isPending && upload.variables?.docType === group.docType}
          uploadPercent={progress?.docType === group.docType ? progress.percent : undefined}
          justSigned={justSigned.has(group.docType)}
          onDownload={download}
          onUpload={(docType, files) => upload.mutate({ docType, files })}
          onDelete={confirmDelete}
          onSetExpiry={(file) => setExpiry({ docId: file.id, initial: file })}
        />
      ))}

      <ExpiryDialog
        opened={expiry !== null}
        initial={expiry?.initial ?? null}
        loading={expiryMutation.isPending}
        onSave={(isPerpetual, date) => {
          if (expiry) {
            expiryMutation.mutate({ docId: expiry.docId, isPerpetual, date });
          }
        }}
        onClose={() => setExpiry(null)}
      />
    </Stack>
  );
}
